import { matchPath } from 'react-router';

import { formatRouteParam } from '../../utils/string.ts';
import { deeplinksSupported } from '../../utils/device.ts';
import { unleash as _unleash } from '../globals.ts'; // include in this bundle chunk

import {
  BRANDSTUDIO_ROUTE_PREFIX,
  getAssetPagePathV2,
} from './getAssetPagePath.ts';
import { tryUrlFromString } from './url.ts';
import { getCategoryShortPath } from './categoryHelpers.ts';

import type { ObjectID } from '@vgtv/api-client';
import type { AssetPagePathParamsV2 } from './getAssetPagePath.ts';
import type { Vendor } from '../../types.ts';
import type { SvpCategoryAdditional } from '@vgtv/api-client/lib/svp_category';

const routeNames = {
  asset: 'asset',
  documentary: 'documentary',
  episode: 'episode',
  category: 'category',
  series: 'series',
  tag: 'tag',
  custom: 'custom',
  myContent: 'myContent',
  search: 'search',
  paywall: 'paywall',
  bigScreen: 'bigScreen',
  news: 'news',
} as const;

interface AssetPageRouteParams {
  vendor?: Vendor;
}

const getAssetPageRoute = ({ vendor }: AssetPageRouteParams = {}): string => {
  let route = '/video/:assetId/:titleShortPath?/:start?';

  if (vendor === 'brandstudio') {
    route = BRANDSTUDIO_ROUTE_PREFIX + route;
  }

  return route;
};

const getDocumentaryPageRoute = (): string => {
  return '/dokumentar/:titleShortPath';
};

const getEpisodePageRoute = (): string => {
  return '/serier/:categoryShortPath/:series/:titleShortPath';
};

const getBigScreenLandingPageRoute = (): string => '/storskjerm';

interface CategoryPagePathParams {
  categoryId: number;
  shortPath?: string | null;
  vendor: Vendor;
}

/**
 * @deprecated try to use {@link getPath} instead
 */
const getCategoryPagePath = ({
  categoryId,
  shortPath,
  vendor,
}: CategoryPagePathParams): string => {
  let route = `/kategori/${categoryId}`;

  if (shortPath) {
    route += `/${shortPath}`;
  }

  if (vendor === 'brandstudio') {
    route = BRANDSTUDIO_ROUTE_PREFIX + route;
  }

  return route;
};

interface CategoryPagePathParamsV2 {
  category: SvpCategoryAdditional;
  vendor: Vendor;
}

const getCategoryPagePathV2 = ({
  category,
  vendor,
}: CategoryPagePathParamsV2): string => {
  const shortPath = getCategoryShortPath(category);

  if (vendor === 'vgtv' && category.isSeries) {
    return `/serier/${shortPath}?categoryId=${category.id}`;
  }

  return getCategoryPagePath({
    categoryId: category.id,
    vendor,
    shortPath,
  });
};

interface CategoryPageRouteParams {
  vendor?: Vendor;
}

const getCategoryPageRoute = ({
  vendor,
}: CategoryPageRouteParams = {}): string => {
  let route = '/kategori/:categoryId/:shortPath?';

  if (vendor === 'brandstudio') {
    route = BRANDSTUDIO_ROUTE_PREFIX + route;
  }

  return route;
};

const getSeriesPageRoute = (): string => {
  return '/serier/:shortPath';
};

const getNewsPageRoute = (): string => {
  return '/nyheter/:shortPath';
};

interface TagPagePathParams {
  tagId: ObjectID;
  title?: string | null;
}

const getTagPagePath = ({ tagId, title }: TagPagePathParams): string => {
  let route = `/tag/${tagId}`;

  if (title) {
    route += `/${formatRouteParam(title)}`;
  }

  return route;
};

const getTagPageRoute = (): string => '/tag/:tagId/:title?';

interface CustomPagePathParams {
  slug: string;
}

const getCustomPagePath = ({ slug }: CustomPagePathParams): string =>
  `/${slug}`;

const getCustomPageRoute = (): string => '/:slug';

const getMyContentPagePath = () => {
  return '/favoritter';
};

const getMyContentPageRoute = () => {
  return '/favoritter';
};

const getSearchPagePath = () => {
  return '/sok';
};

const getSearchPageRoute = () => {
  return '/sok';
};

const getPaywallPagePath = () => {
  return '/kjop-vgpluss-sport';
};

const getPaywallPageRoute = () => {
  return '/kjop-vgpluss-sport';
};

function getPath(
  routeName: typeof routeNames.asset,
  params: AssetPagePathParamsV2
): string;
function getPath(
  routeName: typeof routeNames.category,
  params: CategoryPagePathParamsV2
): string;
function getPath(
  routeName: typeof routeNames.tag,
  params: TagPagePathParams
): string;
function getPath(
  routeName: typeof routeNames.custom,
  params: CustomPagePathParams
): string;
function getPath(routeName: typeof routeNames.myContent): string;
function getPath(routeName: typeof routeNames.search): string;
function getPath(routeName: typeof routeNames.paywall): string;
function getPath(routeName: typeof routeNames.bigScreen): string;
function getPath(
  routeName: keyof typeof routeNames,
  params?:
    | AssetPagePathParamsV2
    | CategoryPagePathParamsV2
    | TagPagePathParams
    | CustomPagePathParams
): string {
  if (routeName === routeNames.asset) {
    return getAssetPagePathV2(params as AssetPagePathParamsV2);
  }
  if (routeName === routeNames.category) {
    return getCategoryPagePathV2(params as CategoryPagePathParamsV2);
  }
  if (routeName === routeNames.tag) {
    return getTagPagePath(params as TagPagePathParams);
  }
  if (routeName === routeNames.custom) {
    return getCustomPagePath(params as CustomPagePathParams);
  }
  if (routeName === routeNames.myContent) {
    return getMyContentPagePath();
  }
  if (routeName === routeNames.search) {
    return getSearchPagePath();
  }
  if (routeName === routeNames.paywall) {
    return getPaywallPagePath();
  }
  if (routeName === routeNames.bigScreen) {
    return getBigScreenLandingPageRoute();
  }

  return '';
}

function pathToUrl(path: string = ''): string {
  if (path.indexOf('://') >= 0) {
    throw new Error('Invalid path provided');
  }

  return window.location.origin + path;
}

interface RouteParams {
  [routeNames.asset]: AssetPageRouteParams;
  [routeNames.category]: CategoryPageRouteParams;
  [routeNames.tag]: never;
  [routeNames.custom]: never;
  [routeNames.myContent]: never;
  [routeNames.search]: never;
  [routeNames.paywall]: never;
  [routeNames.documentary]: never;
  [routeNames.episode]: never;
  [routeNames.series]: never;
  [routeNames.bigScreen]: never;
  [routeNames.news]: never;
}

/**
 * Use a function for generating route to allow for ex. translating routes in future
 */
function getRoute<T extends keyof typeof routeNames>(
  routeName: T,
  params?: RouteParams[T]
): string {
  if (routeName === routeNames.asset) {
    return getAssetPageRoute(params as AssetPageRouteParams);
  }
  if (routeName === routeNames.category) {
    return getCategoryPageRoute(params as CategoryPageRouteParams);
  }
  if (routeName === routeNames.tag) {
    return getTagPageRoute();
  }
  if (routeName === routeNames.custom) {
    return getCustomPageRoute();
  }
  if (routeName === routeNames.myContent) {
    return getMyContentPageRoute();
  }
  if (routeName === routeNames.search) {
    return getSearchPageRoute();
  }
  if (routeName === routeNames.paywall) {
    return getPaywallPageRoute();
  }
  if (routeName === routeNames.documentary) {
    return getDocumentaryPageRoute();
  }
  if (routeName === routeNames.episode) {
    return getEpisodePageRoute();
  }
  if (routeName === routeNames.series) {
    return getSeriesPageRoute();
  }
  if (routeName === routeNames.bigScreen) {
    return getBigScreenLandingPageRoute();
  }
  if (routeName === routeNames.news) {
    return getNewsPageRoute();
  }

  return '';
}

const isAssetPagePath = (url: string) => {
  const matchPageRoute = (route: string) => {
    return matchPath(url.split('?')[0], window.location.origin + route);
  };

  const match =
    matchPageRoute(getAssetPageRoute()) ||
    matchPageRoute(getDocumentaryPageRoute()) ||
    matchPageRoute(getEpisodePageRoute()) ||
    matchPageRoute(getNewsPageRoute());

  return match !== null;
};

const isLocalUrl = (url: string) => {
  if (url.startsWith('/')) {
    return true;
  }

  const parts = tryUrlFromString(url);

  if (!parts) {
    return false;
  }

  if (deeplinksSupported && isAssetPagePath(url)) {
    return false;
  }

  return parts.hostname === window.location.hostname;
};

const getPathFromUrl = (url: string) => {
  try {
    const { pathname, search } = new URL(url);

    return pathname + search;
  } catch {
    return url;
  }
};

export const getStoryUrl = (assetId: number) => {
  return `https://www.vg.no/stories/${assetId}`;
};

export {
  getPath,
  pathToUrl,
  getRoute,
  routeNames,
  getCategoryPagePath,
  getPathFromUrl,
  isLocalUrl,
};
